import {
  useState,
  type KeyboardEvent,
  type ClipboardEvent,
  useMemo,
  useCallback,
} from "react";
import {
  MAX_ATTACHMENTS,
  SUPPORTED_IMAGE_MIME_TYPES,
  type SupportedImageMimeType,
} from "../../../consts";
import {
  convertFileToBase64,
  resizeBase64ImageToBase64,
  type FileData,
} from "./fileToBase64Converter";
import { isMobile } from "@wix/picasso-common";
import { SESSION_STORAGE_KEYS } from "@wix/picasso-common";
import { usePanorama } from "@wix/fe-essentials-standalone";

interface UseImageInputAreaReturn {
  attachmentPreviews: string[];
  handleFileAttachment: (files: FileList | null) => Promise<void>;
  removeAttachment: (index: number) => Promise<void>;
  onEnterPressed: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
  onPastePressed: (e: ClipboardEvent<HTMLTextAreaElement>) => void;
}

const isValidImageType = (
  mimeType: string,
): mimeType is SupportedImageMimeType => {
  return SUPPORTED_IMAGE_MIME_TYPES.includes(
    mimeType as SupportedImageMimeType,
  );
};

export const useImageInputArea = (
  onFileAttachment: (files: string[]) => void,
  handleGenerateSiteClick: () => void,
): UseImageInputAreaReturn => {
  const [attachmentData, setAttachmentData] = useState<FileData[]>([]);
  const { errorMonitor } = usePanorama();
  const errorReporter = errorMonitor({
    data: { tags: { feature: "image-input-area" } },
  });

  const attachmentPreviews = useMemo(
    () => attachmentData.map((file) => file.base64Data),
    [attachmentData],
  );

  const updateAttachments = useCallback(
    async (newData: FileData[]) => {
      try {
        await sessionStorage.setItem(
          SESSION_STORAGE_KEYS.IMAGE_ATTACHMENTS,
          JSON.stringify(newData),
        );
        setAttachmentData(newData);
        onFileAttachment(newData.map((file) => file.base64Data));
      } catch (error) {
        errorReporter.reportError(
          new Error(`Failed to update attachments: ${error}`),
        );
      }
    },
    [onFileAttachment, errorReporter],
  );

  const isDuplicateFile = (
    newFile: FileData,
    existingFiles: FileData[],
  ): boolean => {
    return existingFiles.some(
      (existingFile) =>
        existingFile.name === newFile.name &&
        existingFile.size === newFile.size,
    );
  };

  const onEnterPressed = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && (e.shiftKey || isMobile())) {
      return;
    }
    handleGenerateSiteClick();
  };

  const handleFileAttachment = async (files: FileList | null) => {
    if (files && files.length > 0) {
      const currentCount = attachmentData.length;
      const remainingSlots = MAX_ATTACHMENTS - currentCount;

      if (remainingSlots <= 0) {
        return;
      }

      const supportedFiles = Array.from(files).filter((file) =>
        isValidImageType(file.type),
      );

      if (supportedFiles.length === 0) {
        console.warn(
          "No supported image files selected. Supported formats:",
          SUPPORTED_IMAGE_MIME_TYPES,
        );
        return;
      }

      const newFiles = supportedFiles.slice(0, remainingSlots);

      if (newFiles.length === 0) {
        return;
      }

      const base64Files = await filesToBase64(newFiles);
      const resizedBase64Files = await Promise.all(base64Files);

      const uniqueFiles = resizedBase64Files.filter(
        (file) => !isDuplicateFile(file, attachmentData),
      );

      if (uniqueFiles.length === 0) {
        console.warn("All selected files are duplicates and were skipped.");
        return;
      }

      const updatedData = [...attachmentData, ...uniqueFiles];

      await updateAttachments(updatedData);
    }
  };

  const filesToBase64 = async (files: File[]) => {
    return files.map(async (file) => {
      const base64 = await convertFileToBase64(file);
      if (file.type.startsWith("image/")) {
        const resizedBase64 = await resizeBase64ImageToBase64(base64, 500);
        return resizedBase64;
      }
      return base64;
    });
  };

  const removeAttachment = async (index: number) => {
    const updatedData = attachmentData.filter((_, i) => i !== index);
    await updateAttachments(updatedData);
  };

  const getImageFileFromClipboard = (
    items: DataTransferItemList,
  ): File | null => {
    const fileItem = Array.from(items).pop();
    return fileItem?.getAsFile() || null;
  };

  const createFileListFromFile = (file: File): FileList => {
    const dt = new DataTransfer();
    dt.items.add(file);
    return dt.files;
  };

  const onPastePressed = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const imageFile = getImageFileFromClipboard(items);
    if (!imageFile) return;

    const fileList = createFileListFromFile(imageFile);
    handleFileAttachment(fileList);
  };

  return {
    attachmentPreviews,
    handleFileAttachment,
    removeAttachment,
    onEnterPressed,
    onPastePressed,
  };
};
